/**
 * Digest Preferences - Configure notification digest settings
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface DigestConfig {
  enabled: boolean;
  frequency: string;
  time: string;
  day: number;
}

interface DigestPreferencesProps {
  config: DigestConfig;
  onUpdate: (config: DigestConfig) => void;
}

const FREQUENCY_OPTIONS = [
  { value: 'instant', label: 'Instant', description: 'Get notified immediately for each event' },
  { value: 'hourly', label: 'Hourly', description: 'Receive a summary every hour' },
  { value: 'daily', label: 'Daily', description: 'Receive a daily summary' },
  { value: 'weekly', label: 'Weekly', description: 'Receive a weekly summary' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Generate time options in 30-minute increments
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = (i % 2) * 30;
  const value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const period = hours < 12 ? 'AM' : 'PM';
  const label = `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  return { value, label };
});

export const DigestPreferences: React.FC<DigestPreferencesProps> = ({
  config,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [localConfig, setLocalConfig] = useState<DigestConfig>(config);

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleToggle = () => {
    const newConfig = { ...localConfig, enabled: !localConfig.enabled };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  const handleFrequencyChange = (frequency: string) => {
    const newConfig = { ...localConfig, frequency };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newConfig = { ...localConfig, time: e.target.value };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newConfig = { ...localConfig, day: parseInt(e.target.value, 10) };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  const getNextDigestTime = (): string => {
    if (!localConfig.enabled || localConfig.frequency === 'instant') {
      return '';
    }

    const now = new Date();
    const [hours, minutes] = localConfig.time.split(':').map(Number);

    if (localConfig.frequency === 'hourly') {
      const nextHour = new Date(now);
      nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
      return nextHour.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }

    if (localConfig.frequency === 'daily') {
      const next = new Date(now);
      next.setHours(hours ?? 9, minutes ?? 0, 0, 0);
      if (next <= now) {
        next.setDate(next.getDate() + 1);
      }
      return next.toLocaleDateString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    }

    if (localConfig.frequency === 'weekly') {
      const next = new Date(now);
      const hrs = hours ?? 9;
      const daysUntil = (localConfig.day - now.getDay() + 7) % 7;
      next.setDate(now.getDate() + (daysUntil === 0 && now.getHours() >= hrs ? 7 : daysUntil));
      next.setHours(hrs, minutes ?? 0, 0, 0);
      return next.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }

    return '';
  };

  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('settings.notifications.digest', 'Email Digest')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('settings.notifications.digestDesc', 'Receive a summary of notifications instead of individual emails')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.enabled}
              onChange={handleToggle}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {localConfig.enabled && (
        <div className="p-6 space-y-6">
          {/* Frequency Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('settings.notifications.digestFrequency', 'Frequency')}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FREQUENCY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleFrequencyChange(option.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    localConfig.frequency === option.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      localConfig.frequency === option.value
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {localConfig.frequency === option.value && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {t(`settings.notifications.frequency.${option.value}`, option.label)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t(`settings.notifications.frequencyDesc.${option.value}`, option.description)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Time Selection (for daily/weekly) */}
          {(localConfig.frequency === 'daily' || localConfig.frequency === 'weekly') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {localConfig.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.notifications.digestDay', 'Day of Week')}
                  </label>
                  <select
                    value={localConfig.day}
                    onChange={handleDayChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <option key={day.value} value={day.value}>
                        {t(`common.days.${day.label.toLowerCase()}`, day.label)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings.notifications.digestTime', 'Delivery Time')}
                </label>
                <select
                  value={localConfig.time}
                  onChange={handleTimeChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Next Digest Info */}
          {localConfig.frequency !== 'instant' && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>
                {t('settings.notifications.nextDigest', 'Next digest')}: {getNextDigestTime()}
              </span>
            </div>
          )}

          {/* Sample Digest Preview */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('settings.notifications.digestPreview', 'Sample Digest')}
            </h3>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Your NeoKanban Digest
                </div>
              </div>

              <div className="pl-10 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">3 new task assignments</span>
                  <span className="text-blue-600 dark:text-blue-400">View</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">2 tasks due today</span>
                  <span className="text-blue-600 dark:text-blue-400">View</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">5 new comments on your tasks</span>
                  <span className="text-blue-600 dark:text-blue-400">View</span>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-amber-700 dark:text-amber-300">
                <p className="font-medium">
                  {t('settings.notifications.digestNote', 'Note about urgent notifications')}
                </p>
                <p className="mt-1 text-amber-600 dark:text-amber-400">
                  {t('settings.notifications.digestNoteDesc', 'Urgent notifications (like overdue tasks) will still be sent immediately, even if you have digest enabled.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DigestPreferences;
